<template>
  <div>
    <div class="date-range">
      <span class="title-area" @click="state.showDateRange = !state.showDateRange">
        <i class="fas mr4" :class="{
            'fa-caret-right': !state.showDateRange,
            'fa-caret-down': state.showDateRange,
          }"
        ></i>
        期間<span class="font-sm">{{dateRangeDisp}}</span>
      </span>
      <div class="date-range" v-show="state.showDateRange">
        <MyCalendar
          class="date-from"
          :date-value="state.search.tsFrom"
          :set-date-value-once="false"
          @date-input="onDateParamChange('from', $event)"
        />
        <span class="ib waveline ta-center">〜</span>
        <MyCalendar
          class="date-to"
          :date-value="state.search.tsTo"
          :set-date-value-once="false"
          @date-input="onDateParamChange('to', $event)"
        />
      </div>
    </div>
    <div class="search-type" v-if="searchTypes.length > 0">
      <span class="title-area" @click="state.showSearchTypes = !state.showSearchTypes">
        <i class="fas mr4" :class="{
            'fa-caret-right': !state.showSearchTypes,
            'fa-caret-down': state.showSearchTypes,
          }"
        ></i>
        種別<span class="font-sm">{{selectedSearchTypesDisp}}</span>
      </span>
      <div
        class="custom-control custom-checkbox ml15"
        v-for="e in searchTypes"
        :key="e.key"
        v-show="state.showSearchTypes"
      >
        <input
          type="checkbox"
          class="custom-control-input"
          :id="`${checkboxKeyPrefix}-search-type-${e.key}`"
          :value="e.key"
          v-model="state.search.searchTypes"
          @click="onSearchTypeClick($event)"
          @change="onChange"
        >
        <label class="custom-control-label" :for="`${checkboxKeyPrefix}-search-type-${e.key}`">
          {{e.val}}
        </label>
      </div>
    </div>
    <div class="road-name">
      <span class="title-area" @click="state.showRoads = !state.showRoads">
        <i class="fas mr4" :class="{
            'fa-caret-right': !state.showRoads,
            'fa-caret-down': state.showRoads,
          }"
        ></i>
        路線<span class="font-sm">{{selectedRoadsDisp}}</span>
      </span>
      <template v-if="roadNamesByAreas.length > 0">
        <div v-for="[area, roads] of roadNamesByAreas" :key="area" class="ml12" v-show="state.showRoads">
          <span class="title-area" @click="state.showRoadByAreas[area] = !state.showRoadByAreas[area]">
            <i class="fas mr4" :class="{
                'fa-caret-right': !state.showRoadByAreas[area],
                'fa-caret-down': state.showRoadByAreas[area],
              }"
            ></i>
            {{ area }}
          </span>
          <div
            class="custom-control custom-checkbox ml15"
            v-for="(road, roadIdx) in roads.elems"
            :key="roadIdx"
            v-show="state.showRoadByAreas[area]"
          >
            <input
              type="checkbox"
              class="custom-control-input"
              :id="`${checkboxKeyPrefix}-${area}-${roadIdx}`"
              :value="road"
              v-model="state.search.roads"
              @change="onRoadsOrDirectionsChanged"
            >
            <label class="custom-control-label" :for="`${checkboxKeyPrefix}-${area}-${roadIdx}`">
              {{road.roadNameDispShort}}
            </label>
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="custom-control custom-checkbox ml15"
          v-for="(e, idx) in masters.kp.roadNameDispMap"
          :key="idx"
          v-show="state.showRoads"
        >
          <input
            type="checkbox"
            class="custom-control-input"
            :id="`${checkboxKeyPrefix}-roads-${idx}`"
            :value="e"
            v-model="state.search.roads"
            @change="onRoadsOrDirectionsChanged"
          >
          <label class="custom-control-label" :for="`${checkboxKeyPrefix}-roads-${idx}`">
            {{e.roadNameDispShort}}
          </label>
        </div>
      </template>
    </div>
    <div class="direction">
      <span class="title-area" @click="state.showDirections = !state.showDirections">
        <i class="fas mr4" :class="{
            'fa-caret-right': !state.showDirections,
            'fa-caret-down': state.showDirections,
          }"
        ></i>
        方向<span class="font-sm">{{selectedDirectionsDisp}}</span>
      </span>
      <div
        class="custom-control custom-checkbox ml15"
        v-for="(e, idx) in directionOptions"
        :key="idx"
        v-show="state.showDirections"
      >
        <input
          type="checkbox"
          class="custom-control-input"
          :id="`${checkboxKeyPrefix}-directions-${idx}`"
          :value="e"
          v-model="state.search.directions"
          @change="onRoadsOrDirectionsChanged"
        >
        <label class="custom-control-label" :for="`${checkboxKeyPrefix}-directions-${idx}`">{{e}}</label>
      </div>
    </div>
    <div class="kp" v-if="isKpSearch">
      <span class="title-area" @click="state.showKpRange = !state.showKpRange">
        <i class="fas mr4" :class="{
            'fa-caret-right': !state.showKpRange,
            'fa-caret-down': state.showKpRange,
          }"
        ></i>
        キロポスト<span class="font-sm">{{kpRangeDisp}}</span>
      </span>
      <div class="form-group mb0 mr16 ml15" v-show="state.showKpRange">
        <div class="ws-nowrap">
          <input
            v-model="state.search.kpCalcFrom"
            type="text"
            class="ib form-control kp-calc-from"
            :disabled="disabledKpFields"
            @change="onChange"
          >
          <span class="ib waveline ta-center">〜</span>
          <input
            v-model="state.search.kpCalcTo"
            type="text"
            class="ib form-control kp-calc-to"
            :disabled="disabledKpFields"
            @change="onChange"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  watch,
  watchEffect,
  computed,
  onMounted,
} from 'vue';
import type { RoadName, RoadNameDisp } from 'src/models/master';
import type { CommonSearchParams, SearchType, SearchTypeMap } from 'src/models/geoItemSearch';
import { useReactiveMasters } from 'src/composables/useMasters';
import { dtFormat } from 'src/lib/dateHelper';

interface SearchParams {
  searchTypes: Array<string | number>;
  tsFrom: Date | null;
  tsTo: Date | null;
  roads: RoadName[];
  directions: string[];
  // キロポストは入力欄の生の値。親へ渡すときに数値化する
  kpCalcFrom: string;
  kpCalcTo: string;
}

interface IndexRoadName {
  idx: number;
  elems: RoadNameDisp[];
}

type RoadNamesByAreaMap = Record<string, IndexRoadName>;

interface GeoItemSearchCommentState {
  showSearchTypes: boolean;
  showDateRange: boolean;
  showRoads: boolean;
  showRoadByAreas: Record<string, boolean>;
  showDirections: boolean;
  showKpRange: boolean;
  search: SearchParams;
}

// withDefaultsでdefaultを与えても、TS上optional(?)でないとrequired:trueのままになるVueの仕様
// https://github.com/vuejs/core/blob/v3.5.35/packages/compiler-sfc/src/script/resolveType.ts (required: !e.optional)
// defaultを持つpropsはoptionalにしている(checkboxKeyPrefixは全呼び出し元が渡しているため必須のまま)
interface Props {
  checkboxKeyPrefix: string | null;
  searchTypeMap?: SearchTypeMap;
  searchTypes?: Array<SearchType>;
  searchTypeDefaultValues?: Array<string | number>;
  isKpSearch?: boolean;
  defaultDaysBefore?: number;
  defaultMonthsBefore?: number;
  maxSearchRangeMonths?: number;
}

const props = withDefaults(defineProps<Props>(), {
  searchTypeMap: () => ({}),
  searchTypes: () => [],
  searchTypeDefaultValues: () => [],
  isKpSearch: true,
  defaultDaysBefore: 0,
  defaultMonthsBefore: 3,
  maxSearchRangeMonths: 3,
});

const emit = defineEmits(['change']);

const initSearchState = (): SearchParams => {
  return {
    searchTypes: [],
    tsFrom: null,
    tsTo: null,
    roads: [],
    directions: [],
    kpCalcFrom: '',
    kpCalcTo: '',
  };
};
const state = reactive<GeoItemSearchCommentState>({
  showSearchTypes: false,
  showDateRange: false,
  showRoads: false,
  showRoadByAreas: {},
  showDirections: false,
  showKpRange: false,
  search: initSearchState(),
});

const getRoadNamesByAreas = (): [string, IndexRoadName][] => {
  let areaIdx = 0;
  const roadNamesByAreaMap = Object.values(masters.kp.roadNameDispMap).reduce((acc: RoadNamesByAreaMap, e) => {
    if (!acc[e.area]) {
      acc[e.area] = { idx: areaIdx++, elems: [] };
    }
    acc[e.area].elems.push(e);
    return acc;
  }, {});
  const areas = Object.keys(roadNamesByAreaMap);
  if (areas.length < 2) {
    return [];
  }
  return Object.entries(roadNamesByAreaMap).sort((a, b) => a[1].idx < b[1].idx ? -1 : 1);
};

const roadNamesByAreas = computed<[string, IndexRoadName][]>(getRoadNamesByAreas);

const setInitialSearchParams = () => {
  const now = new Date();
  const tsFrom = props.defaultDaysBefore > 0
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - props.defaultDaysBefore)
    : new Date(now.getFullYear(), now.getMonth() - props.defaultMonthsBefore, now.getDate());
  const params = {
    searchTypes: props.searchTypeDefaultValues.length > 0
      ? props.searchTypeDefaultValues
      : props.searchTypes.map(e => e.key),
    tsFrom: tsFrom,
    tsTo: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    roads: [],
    directions: [],
    kpCalcFrom: '',
    kpCalcTo: '',
  };
  state.search = params;
};

const { masters, resolveMastersPromise } = useReactiveMasters();
onMounted(async() => {
  await resolveMastersPromise();
  state.showRoadByAreas = roadNamesByAreas.value.reduce((acc: Record<string, boolean>, curr) => {
    acc[curr[0]] = false;
    return acc;
  }, {});
  setInitialSearchParams();
});
watch(() => props.searchTypes, () => {
  state.search.searchTypes = props.searchTypes.map(e => e.key);
});

// computed
const selectedSearchTypesDisp = computed(() => {
  if (state.search.searchTypes.length === 0) return '';

  const ret = state.search.searchTypes.slice().sort((a, b) => {
    const val1 = props.searchTypeMap[a].dispOrder;
    const val2 = props.searchTypeMap[b].dispOrder;
    return val1 < val2 ? -1 : val1 > val2 ? 1 : 0;
  }).map(e => props.searchTypeMap[e].val).join(', ');
  return `（${ret}）`;
});
const selectedRoadsDisp = computed(() => {
  if (state.search.roads.length === 0) return '';

  const ret = state.search.roads.map(e => e.roadNameDispShort).join(', ');
  return `（${ret}）`;
});
const selectedDirectionsDisp = computed(() => {
  if (state.search.directions.length === 0) return '';

  const ret = state.search.directions.join(', ');
  return `（${ret}）`;
});
const kpRangeDisp = computed(() => {
  if (!state.search.kpCalcFrom && !state.search.kpCalcTo) return '';

  return `（${state.search.kpCalcFrom}〜${state.search.kpCalcTo}）`;
});
const directionOptions = computed(() =>
  [...new Set(state.search.roads.flatMap(e => e.directions))],
);
const dateRangeDisp = computed(() => {
  const tsFrom = dtFormat(state.search.tsFrom, 'yyyy/mm/dd');
  const tsTo = dtFormat(state.search.tsTo, 'yyyy/mm/dd');
  const ret = tsFrom + '〜' + tsTo;
  return `（${ret}）`;
});
const disabledKpFields = computed(() => {
  return state.search.roads.length === 0 || state.search.directions.length === 0;
});

// methods
// 親（GeoItemSearch*）へは v-model:search-params で派生パラメータを反映する（命令的な ref 呼び出しを廃止）
const searchParamsModel = defineModel<CommonSearchParams | null>('searchParams', { default: null });
// 未入力・数値でない入力は「範囲指定なし」として null にする。
// 空文字のまま送ると Laravel 側で null になり、検索条件の値として使えない
const toKpCalc = (val: string): number | null => {
  const num = parseFloat(val);
  return isNaN(num) ? null : num;
};
// flush: 'sync' で state.search 変更を同期的に model へ反映する。
// デフォルトの 'pre' はマイクロタスクに遅延するため、同期的な @change → getSearchParams()
// のチェーンが 1 つ前の検索条件を読んでしまう不具合を防ぐ。
watchEffect(() => {
  searchParamsModel.value = {
    searchTypes: state.search.searchTypes,
    tsFrom: state.search.tsFrom,
    tsTo: state.search.tsTo ? new Date(state.search.tsTo.valueOf() + 86400 * 1000) : state.search.tsTo,
    roadNameDisps: state.search.roads.map(e => e.roadNameDisp),
    directions: state.search.directions,
    kpCalcFrom: toKpCalc(state.search.kpCalcFrom),
    kpCalcTo: toKpCalc(state.search.kpCalcTo),
  };
}, { flush: 'sync' });
const onSearchTypeClick = (e: MouseEvent) => {
  if (state.search.searchTypes.length > 1) return;
  if ((e.target as HTMLInputElement).checked) return;

  // 種別は最低一つ選択必須
  e.preventDefault();
  e.stopPropagation();
};
const adjustSearchDateRange = (prop: string) => {
  const [propVal, otherPropVal, setToOtherProp, sign] =
    prop === 'from'
      ? [state.search.tsFrom, state.search.tsTo, (arg: Date) => { state.search.tsTo = arg; }, 1]
      : [state.search.tsTo, state.search.tsFrom, (arg: Date) => { state.search.tsFrom = arg; }, -1];
  if (!propVal) return;

  const borderRangeDate = new Date(
    propVal.getFullYear(),
    propVal.getMonth() + sign * props.maxSearchRangeMonths,
    propVal.getDate(),
  );

  // 編集していない方の日付が正しい日付ではない場合、編集していない方の日付を調整する
  if (!otherPropVal) {
    setToOtherProp(borderRangeDate);
    return;
  }

  const params = state.search;
  // 開始日 > 終了日の場合、編集していない方の日付を調整する
  if (params.tsFrom && params.tsTo && params.tsFrom > params.tsTo) {
    setToOtherProp(borderRangeDate);
    return;
  }

  // 開始日 <= 終了日 の場合、borderRangeDateの範囲を超えないよう、編集していない方の日付を調整する
  if (prop === 'from') {
    setToOtherProp(new Date(Math.min(otherPropVal.getTime(), borderRangeDate.getTime())));
  } else {
    setToOtherProp(new Date(Math.max(otherPropVal.getTime(), borderRangeDate.getTime())));
  }
};
const onChange = () => {
  emit('change');
};
const onDateParamChange = (prop: string, val: Date) => {
  if (prop === 'from') {
    state.search.tsFrom = val;
  } else if (prop === 'to') {
    state.search.tsTo = val;
  }

  adjustSearchDateRange(prop);

  if (state.search.tsFrom && state.search.tsTo) {
    onChange();
  }
};
const onRoadsOrDirectionsChanged = () => {
  if (disabledKpFields.value) {
    state.search.kpCalcFrom = '';
    state.search.kpCalcTo = '';
  }
  onChange();
};
</script>

<style lang="scss" scoped>
.title-area {
  cursor: pointer;
}
.font-sm {
  font-size: 12px;
}
.fa-caret-down {
  margin: 0 2px 0 -2px;
}
.date-range {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  .waveline { width: 20px; }
  .date-from, .date-to { width: 96px; }
}
.direction {
  margin-left: 12px;
}
.kp {
  margin-left: 24px;
  .waveline { width: 20px; }
  .kp-calc-from, .kp-calc-to { width: 64px; }
}
</style>
